import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../../hooks';
import proofkitFilemakerService from '../../../services/proofkitFilemakerService';
import { RefreshCw, Plus, Eye, Edit, Trash, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Input } from '../../../components/ui';

const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";
const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";
const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";

export default function ContactsList() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [contactSegments, setContactSegments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const [contactsRes, segmentsRes, contactSegmentsRes] = await Promise.all([
        proofkitFilemakerService.getAllRecords(CONTACTS_LAYOUT),
        proofkitFilemakerService.getAllRecords(SEGMENTS_LAYOUT),
        proofkitFilemakerService.getAllRecords(CONTACT_SEGMENTS_LAYOUT),
      ]);

      setContacts(Array.isArray(contactsRes?.data) ? contactsRes.data : []);
      setSegments(Array.isArray(segmentsRes?.data) ? segmentsRes.data : []);
      setContactSegments(Array.isArray(contactSegmentsRes?.data) ? contactSegmentsRes.data : []);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los contactos desde FileMaker",
        variant: "destructive",
      });
      setContacts([]);
      setSegments([]);
      setContactSegments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const segmentsById = useMemo(() => {
    const map = {};
    segments.forEach((row) => {
      const fd = row?.fieldData || {};
      const segId = fd?.ClavePrincipal || row?.recordId;
      const nombre = fd?.nombre || "";
      if (segId) map[String(segId)] = nombre;
    });
    return map;
  }, [segments]);

  const contactSegmentsMap = useMemo(() => {
    const map = {};
    contactSegments.forEach((row) => {
      const fd = row?.fieldData || {};
      const contactId = fd?.contact_id;
      const segmentId = fd?.segment_id;
      if (!contactId || !segmentId) return;
      const segName = segmentsById[String(segmentId)] || "";
      if (!segName) return;
      const key = String(contactId);
      map[key] = map[key] || [];
      if (!map[key].includes(segName)) {
        map[key].push(segName);
      }
    });
    return map;
  }, [contactSegments, segmentsById]);

  const contactsData = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    const base = (Array.isArray(contacts) ? contacts : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const rid = r?.recordId || "";
      const key = fieldData?.ClavePrincipal || "";
      const nombre = fieldData?.nombre || "";
      const apellido = fieldData?.apellido || "";
      const email = fieldData?.email || "";
      const telefono = fieldData?.telefono || "";
      const empresa = fieldData?.empresa || "";
      const estado = fieldData?.estado || "activo";
      const fullName = `${nombre} ${apellido}`.trim();
      const segments = contactSegmentsMap[key] || [];
      
      return {
        id: rid,
        key,
        nombre,
        apellido,
        email,
        telefono,
        empresa,
        estado,
        fullName,
        segments,
      };
    });

    if (!q) return base;

    return base.filter((c) => {
      const searchText = `${c.fullName} ${c.email} ${c.empresa}`.toLowerCase();
      return searchText.includes(q);
    });
  }, [contacts, searchQuery, contactSegmentsMap]);

  const getEstadoBadge = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "activo") return <Badge variant="default">Activo</Badge>;
    if (v === "inactivo") return <Badge variant="secondary">Inactivo</Badge>;
    if (v === "deshabilitado") return <Badge variant="destructive">Deshabilitado</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("¿Estás seguro de eliminar este contacto?")) {
      return;
    }

    try {
      await proofkitFilemakerService.deleteRecord(CONTACTS_LAYOUT, contactId);
      toast({
        title: "Contacto eliminado",
        description: "El contacto fue eliminado exitosamente",
      });
      fetchContacts();
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el contacto",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">Contactos</h1>
          <div className="relative w-full max-w-sm">
            <Input
              placeholder="Buscar contactos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContacts} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => navigate("/campanas/contacts/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Contacto
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gestionar Contactos</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando contactos...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Segmentos</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactsData.length ? (
                    contactsData.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="font-medium">{contact.fullName || "Sin nombre"}</TableCell>
                        <TableCell>{contact.email || "-"}</TableCell>
                        <TableCell>{contact.telefono || "-"}</TableCell>
                        <TableCell>{contact.empresa || "-"}</TableCell>
                        <TableCell>
                          {contact.segments.length ? (
                            <div className="flex flex-wrap gap-1">
                              {contact.segments.map((segment, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {segment}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{getEstadoBadge(contact.estado)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/campanas/contacts/${contact.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/campanas/contacts/${contact.id}/segments`)}
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/campanas/contacts/${contact.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteContact(contact.id)}
                            >
                              <Trash className="h-4 w-4 text-[var(--yool-error)]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No hay contactos.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
